import ListItem from '@mui/material/ListItem';
import { Link } from 'react-router-dom';
import { Link as RouterLink } from 'react-scroll';
import Box from '@mui/material/Box';
import { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import List from '@mui/material/List';
import { ListItemIcon, Typography } from '@mui/material';
import { useRecoilValue } from 'recoil';
import {
  siteState,
  speciesPercentageDateRangeState,
} from '../state/statistics.state';
import HomeIcon from '@mui/icons-material/Home';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CameraswitchIcon from '@mui/icons-material/Cameraswitch';
import FlutterDashIcon from '@mui/icons-material/FlutterDash';
import BarChartIcon from '@mui/icons-material/BarChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FlightIcon from '@mui/icons-material/Flight';
import HeightIcon from '@mui/icons-material/Height';
import AirIcon from '@mui/icons-material/Air';
import CompassCalibrationIcon from '@mui/icons-material/CompassCalibration';
import CategoryIcon from '@mui/icons-material/Category';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import ImageIcon from '@mui/icons-material/Image';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { useFeatureFlag } from '../utils/featureFlagApi';
import { GetSiteData } from '../hooks/statistics.hooks';


interface MainListItemsProps {
  currentRouteIndex: number | undefined;
  isCollapsed?: boolean;
}

function MainListItems({ currentRouteIndex, isCollapsed = false }: MainListItemsProps) {
  const site = useRecoilValue(siteState);
  const siteQuery = GetSiteData();
  const siteData = siteQuery?.data;

  const currentSite = siteData?.data?.find((s: any) => s.id === parseInt(site));
  const companyId = currentSite?.company_id || 0;

  const turbineInteractionFeature = useFeatureFlag(companyId, 'turbine_interaction_events');
  const turbineInteractionEnabled = turbineInteractionFeature?.data === true;
  const turbineInteractionIndex = turbineInteractionEnabled ? 2 : -1;

  const TurbineInteractionIcon = () => (
    <Box sx={{ position: 'relative', width: 24, height: 24 }}>
      <CameraswitchIcon sx={{ fontSize: 24, color: selectedIndex === turbineInteractionIndex ? '#7800F0' : '#6B7280' }} />
      <FlutterDashIcon sx={{ position: 'absolute', right: -4, bottom: -4, fontSize: 14, color: selectedIndex === turbineInteractionIndex ? '#9C7AE0' : '#9CA3AF' }} />
    </Box>
  );
  const StyledList = styled(List)({
    '&& .Mui-selected, && .Mui-selected:hover': {
      backgroundColor: 'rgba(120, 0, 240, 0.08)',
      color: '#7800F0',
      borderRadius: '12px',
      margin: '4px 8px',
      '&, & .MuiListItemIcon-root': {
        color: '#7800F0',
      },
      '& .MuiTypography-root': {
        color: '#7800F0',
        fontWeight: 600,
      },
    },
    '& .MuiListItem-root:hover': {
      backgroundColor: 'rgba(120, 0, 240, 0.04)',
      borderRadius: '12px',
      margin: '4px 8px',
      transition: 'all 0.2s ease-in-out',
    },
    '& .MuiListItem-root': {
      margin: '4px 8px',
      borderRadius: '12px',
      transition: 'all 0.2s ease-in-out',
    },
  });

  const [selectedIndex, setSelectedIndex] = useState(currentRouteIndex);

  useEffect(() => {
    setSelectedIndex(currentRouteIndex);
  }, [currentRouteIndex]);

  const handleListItemClick = (event: any, index: any) => {
    setSelectedIndex(index);
  };

  const baseMenuItems = [
    {
      index: 0,
      to: "/",
      icon: <HomeIcon sx={{ fontSize: 24 }} />,
      text: "Dashboard",
      description: "Overview and insights"
    },
    {
      index: 1,
      to: "/recent-events",
      icon: <EventNoteIcon sx={{ fontSize: 24 }} />,
      text: "Recent Events",
      description: "Latest detections"
    },
    {
      index: 2,
      to: "/statistics",
      icon: <BarChartIcon sx={{ fontSize: 24 }} />,
      text: "Statistics",
      description: "Data analysis"
    },
  ];

  const turbineInteractionMenuItem = {
    index: 2,
    to: "/turbine-interaction",
    icon: <TurbineInteractionIcon />,
    text: "Turbine Interaction Events",
    description: "Latest interactions"
  };

  const extendedMenuItems = turbineInteractionEnabled
    ? [
        baseMenuItems[0],
        baseMenuItems[1],
        turbineInteractionMenuItem,
        { ...baseMenuItems[2], index: 3 },
      ]
    : baseMenuItems;

  const menuItems = extendedMenuItems.map((item, orderIndex) => ({
    ...item,
    index: orderIndex,
  }));

  return (
    <StyledList>
      <Box sx={{
        padding: isCollapsed ? '8px 12px' : '12px 16px',
        color: '#17052D',
        background: 'linear-gradient(180deg, rgba(248, 249, 250, 0.8) 0%, rgba(255, 255, 255, 0.4) 100%)',
        borderRadius: '16px',
        margin: isCollapsed ? '2px 6px 2px 2px' : '4px 12px',
        border: '1px solid rgba(229, 231, 235, 0.6)'
      }}>

        {menuItems.map((item) => (
            <ListItem
              key={item.index}
              component={Link}
              to={item.to}
              button
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                columnGap: isCollapsed ? '0' : '16px',
                padding: '10px 10px 10px 10px',
                borderRadius: '12px',
                marginBottom: '2px',
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                '&:hover': {
                  transform: isCollapsed ? 'scale(1.1)' : 'translateX(4px)',
                },
              }}
              selected={selectedIndex === item.index}
              onClick={(event: any) => handleListItemClick(event, item.index)}
            >
              <ListItemIcon sx={{
                minWidth: 'auto',
                color: selectedIndex === item.index ? '#7800F0' : '#6B7280'
              }}>
                {item.icon}
              </ListItemIcon>

              <Box sx={{
                display: isCollapsed ? 'none' : 'flex',
                flexDirection: 'column',
                flex: 1
              }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: selectedIndex === item.index ? 600 : 500,
                    color: selectedIndex === item.index ? '#7800F0' : '#17052D',
                    lineHeight: '20px',
                  }}
                >
                  {item.text}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '11px',
                    color: '#6B7280',
                    lineHeight: '16px',
                    marginTop: '2px',
                  }}
                >
                  {item.description}
                </Typography>
              </Box>
            </ListItem>
        ))}
      </Box>
    </StyledList>
  );
}

const SecondaryListItems = () => {
  const speciesPercentageDateRange = useRecoilValue(
    speciesPercentageDateRangeState,
  );
  const site = useRecoilValue(siteState);

  const { data: siteData } = GetSiteData();
  
  // Get company ID for the current site
  const currentSite = siteData?.data?.find((s: any) => s.id === parseInt(site));
  const companyId = currentSite?.company_id || 0;

  // Feature flags
  const { data: heightGraphV1Enabled } = useFeatureFlag(companyId, 'height_graph_v1');
  const { data: heightGraphV2Enabled } = useFeatureFlag(companyId, 'height_graph_v2');
  const { data: heightGraphV3Enabled } = useFeatureFlag(companyId, 'height_graph_v3');
  const { data: collisionRiskEnabled } = useFeatureFlag(companyId, 'collision_risk_single_cam');

  const sectionItems = [
    {
      title: "BIRDS DETECTED",
      icon: <TrendingUpIcon sx={{ fontSize: 16 }} />,
      items: [
        { to: "yearly-bird-count", text: "Count of birds", icon: <BarChartIcon sx={{ fontSize: 16 }} /> },
        ...(collisionRiskEnabled ? [
          { to: "bird-at-collision-risk-count", text: "Count of collision risk birds", icon: <FlightIcon sx={{ fontSize: 16 }} /> }
        ] : []),
        { to: "monthly-bird-count", text: "Average number of birds", icon: <TrendingUpIcon sx={{ fontSize: 16 }} /> },
        { to: "observation-rate", text: "Observation rate", icon: <BarChartIcon sx={{ fontSize: 16 }} /> }
      ]
    },
    {
      title: "FLIGHT DIRECTION AND HEIGHT",
      icon: <FlightIcon sx={{ fontSize: 16 }} />,
      items: [
        { to: "direction-radar-chart", text: "Flight direction", icon: <CompassCalibrationIcon sx={{ fontSize: 16 }} /> },
        ...(heightGraphV1Enabled ? [
          { to: "bird-height-distribution", text: "Flight height", icon: <HeightIcon sx={{ fontSize: 16 }} /> }
        ] : []),
        ...(heightGraphV2Enabled ? [
          { to: "bird-height-distribution-v2", text: "Flight height v2", icon: <HeightIcon sx={{ fontSize: 16 }} /> }
        ] : []),
        ...(heightGraphV3Enabled ? [
          { to: "bird-height-distribution-v3", text: "Flight height", icon: <HeightIcon sx={{ fontSize: 16 }} /> }
        ] : [])
      ]
    },
    {
      title: "WEATHER CORRELATIONS",
      icon: <AirIcon sx={{ fontSize: 16 }} />,
      items: [
        { to: "wind-speed-wrapper", text: "Wind speed range", icon: <AirIcon sx={{ fontSize: 16 }} /> },
        { to: "wind-direction-radar-chart", text: "Wind direction", icon: <CompassCalibrationIcon sx={{ fontSize: 16 }} /> }
      ]
    },
    ...(speciesPercentageDateRange && speciesPercentageDateRange.length > 0 ? [{
      title: "SPECIES SPECIFIC DATA",
      icon: <CategoryIcon sx={{ fontSize: 16 }} />,
      items: [
        { to: "species-data-wrapper", text: "Count of species", icon: <CategoryIcon sx={{ fontSize: 16 }} /> }
      ]
    }] : [])
  ];

  return (
    <Box sx={{
      padding: '16px 12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '24px'
    }}>
      {sectionItems.map((section, sectionIndex) => (
        <Box key={sectionIndex} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            marginBottom: '12px',
            paddingLeft: '16px'
          }}>
            <Box sx={{ 
              color: '#6B7280',
              display: 'flex',
              alignItems: 'center'
            }}>
              {section.icon}
            </Box>
            <Typography
              sx={{
                color: '#6B7280',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.8px',
                textTransform: 'uppercase'
              }}
            >
              {section.title}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {section.items.map((item, itemIndex) => (
              <ListItem
                key={itemIndex}
                sx={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  margin: '0 12px',
                  '&:hover': {
                    backgroundColor: 'rgba(120, 0, 240, 0.04)',
                    transform: 'translateX(4px)',
                    transition: 'all 0.2s ease-in-out',
                  },
                }}
              >
                <RouterLink
                  activeClass="scroll-active"
                  smooth
                  spy
                  to={item.to}
                  offset={-100}
                  onSetActive={() => {}}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    textDecoration: 'none',
                    color: 'inherit'
                  }}
                >
                  <Box sx={{
                    color: '#6B7280',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {item.icon}
                  </Box>
                  <span className="list-item-text" style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#374151',
                    lineHeight: '16px'
                  }}>
                    {item.text}
                  </span>
                </RouterLink>
              </ListItem>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const RecentEventsListItems = () => {
  const sectionItems = [
    {
      title: "RECENT EVENTS SECTIONS",
      icon: <EventNoteIcon sx={{ fontSize: 16 }} />,
      items: [
        { to: "video-section", text: "Video", icon: <VideoLibraryIcon sx={{ fontSize: 16 }} /> },
        { to: "image-section", text: "Image", icon: <ImageIcon sx={{ fontSize: 16 }} /> },
        { to: "detections-section", text: "Detections", icon: <ListAltIcon sx={{ fontSize: 16 }} /> }
      ]
    }
  ];

  return (
    <Box sx={{
      padding: '16px 12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '24px'
    }}>
      {sectionItems.map((section, sectionIndex) => (
        <Box key={sectionIndex} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            marginBottom: '12px',
            paddingLeft: '16px'
          }}>
            <Box sx={{ 
              color: '#6B7280',
              display: 'flex',
              alignItems: 'center'
            }}>
              {section.icon}
            </Box>
            <Typography
              sx={{
                color: '#6B7280',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.8px',
                textTransform: 'uppercase'
              }}
            >
              {section.title}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {section.items.map((item, itemIndex) => (
              <ListItem
                key={itemIndex}
                sx={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  margin: '0 12px',
                  '&:hover': {
                    backgroundColor: 'rgba(120, 0, 240, 0.04)',
                    transform: 'translateX(4px)',
                    transition: 'all 0.2s ease-in-out',
                  },
                }}
              >
                <RouterLink
                  activeClass="scroll-active"
                  smooth
                  spy
                  to={item.to}
                  offset={-100}
                  onSetActive={() => {}}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    textDecoration: 'none',
                    color: 'inherit'
                  }}
                >
                  <Box sx={{
                    color: '#6B7280',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {item.icon}
                  </Box>
                  <span className="list-item-text" style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#374151',
                    lineHeight: '16px'
                  }}>
                    {item.text}
                  </span>
                </RouterLink>
              </ListItem>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export { MainListItems, SecondaryListItems, RecentEventsListItems };
